/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DockableMessagesService {
  private selectionMismatchCodeCheckingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private invalidSelectionSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private selectionMismatchConduitsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private specificationMissingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private selectionMismatchEquipmentSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private familyNotExistsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private codeCheckingExceedingMaximumAngleSubject: BehaviorSubject<[boolean, number[]]> = new BehaviorSubject<[boolean, number[]]>([false, [0, 0]]);
  private processingApplySystemDataSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private updatingNominalSizeStatusSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private updatingExtendedDataStatusSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private wireFillSpecInformationNotAvailableSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private commentsOrConduitIdMissingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private conduitFillExceeds40PercentCheckingOrNotErrorSubject: BehaviorSubject<[boolean, boolean]> = new BehaviorSubject<[boolean, boolean]>([false, false]);
  private unableToProcessFittingsErrorSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public selectionMismatchCodeChecking$: Observable<boolean> = this.selectionMismatchCodeCheckingSubject.asObservable();
  public invalidSelection$: Observable<boolean> = this.invalidSelectionSubject.asObservable();
  public selectionMismatchConduits$: Observable<boolean> = this.selectionMismatchConduitsSubject.asObservable();
  public specificationMissing$: Observable<boolean> = this.specificationMissingSubject.asObservable();
  public selectionMismatchEquipment$: Observable<boolean> = this.selectionMismatchEquipmentSubject.asObservable();
  public familyNotExists$: Observable<boolean> = this.familyNotExistsSubject.asObservable();
  public wireFillSpecInformationNotAvailable$: Observable<boolean> = this.wireFillSpecInformationNotAvailableSubject.asObservable();
  public commentsOrConduitIdMissing$: Observable<boolean> = this.commentsOrConduitIdMissingSubject.asObservable();
  public conduitFillExceeds40PercentCheckingOrNotError$: Observable<[boolean, boolean]> = this.conduitFillExceeds40PercentCheckingOrNotErrorSubject.asObservable();
  public codeCheckingExceedingMaximumAngle$: Observable<[boolean, number[]]> = this.codeCheckingExceedingMaximumAngleSubject.asObservable();
  public processingApplySystemData$: Observable<number> = this.processingApplySystemDataSubject.asObservable();
  public updatingNominalSizeStatus$: Observable<number> = this.updatingNominalSizeStatusSubject.asObservable();
  public updatingExtendedDataStatus$: Observable<number> = this.updatingExtendedDataStatusSubject.asObservable();
  public unableToProcessFittingsError$: Observable<boolean> = this.unableToProcessFittingsErrorSubject.asObservable();

  public setSelectionMismatchCodeChecking(value: boolean): void {
    this.selectionMismatchCodeCheckingSubject.next(value);
  }

  public getSelectionMismatchCodeChecking(): boolean {
    return this.selectionMismatchCodeCheckingSubject.getValue();
  }

  public setInvalidSelection(value: boolean): void {
    this.invalidSelectionSubject.next(value);
  }

  public getInvalidSelection(): boolean {
    return this.invalidSelectionSubject.getValue();
  }

  public setSelectionMismatchConduits(value: boolean): void {
    this.selectionMismatchConduitsSubject.next(value);
  }

  public getSelectionMismatchConduits(): boolean {
    return this.selectionMismatchConduitsSubject.getValue();
  }

  public setSpecificationMissing(value: boolean): void {
    this.specificationMissingSubject.next(value);
  }

  public getSpecificationMissing(): boolean {
    return this.specificationMissingSubject.getValue();
  }

  public setSelectionMismatchEquipment(value: boolean): void {
    this.selectionMismatchEquipmentSubject.next(value);
  }

  public getSelectionMismatchEquipment(): boolean {
    return this.selectionMismatchEquipmentSubject.getValue();
  }

  public setFamilyNotExists(value: boolean): void {
    this.familyNotExistsSubject.next(value);
  }

  public getFamilyNotExists(): boolean {
    return this.familyNotExistsSubject.getValue();
  }

  public setCodeCheckingExceedingMaximumAngle(isExceeded: boolean, maxAngle: number, bendAngle: number): void {
    this.codeCheckingExceedingMaximumAngleSubject.next([isExceeded, [maxAngle, bendAngle]]);
  }

  public getCodeCheckingExceedingMaximumAngle(): [boolean, number[]] {
    return this.codeCheckingExceedingMaximumAngleSubject.getValue();
  }

  public setProcessingApplySystemData(value: number): void {
    this.processingApplySystemDataSubject.next(value);
  }

  public getProcessingApplySystemData(): number {
    return this.processingApplySystemDataSubject.getValue();
  }

  public setUpdatingNominalSizeStatus(totalCount: number): void {
    this.updatingNominalSizeStatusSubject.next(totalCount);
  }

  public getUpdatingNominalSizeStatus(): number {
    return this.updatingNominalSizeStatusSubject.getValue();
  }

  public setUpdatingExtendedData(totalCount: number): void {
    this.updatingExtendedDataStatusSubject.next(totalCount);
  }

  public getUpdatingExtendedData(): number {
    return this.updatingExtendedDataStatusSubject.getValue();
  }

  public setWireFillSpecInformationNotAvailable(value: boolean): void {
    this.wireFillSpecInformationNotAvailableSubject.next(value);
  }

  public getWireFillSpecInformationNotAvailable(): boolean {
    return this.wireFillSpecInformationNotAvailableSubject.getValue();
  }

  public setCommentsOrConduitIdMissing(value: boolean): void {
    this.commentsOrConduitIdMissingSubject.next(value);
  }

  public getCommentsOrConduitIdMissing(): boolean {
    return this.commentsOrConduitIdMissingSubject.getValue();
  }

  public setConduitFillExceeds40PercentCheckingOrNotError(isExceeded: boolean,isNecMaximumFill: boolean): void {
    this.conduitFillExceeds40PercentCheckingOrNotErrorSubject.next([isExceeded,isNecMaximumFill]);
  }

  public getConduitFillExceeds40PercentCheckingOrNotError(): [boolean,boolean] {
    return this.conduitFillExceeds40PercentCheckingOrNotErrorSubject.getValue();
  }
 
  public setUnableToProcessFittingsError(value: boolean): void {
    this.unableToProcessFittingsErrorSubject.next(value);
  }

  public getUnableToProcessFittingsError(): boolean {
    return this.unableToProcessFittingsErrorSubject.getValue();
  }
}
